import { useEffect, useState } from "react";
import { useAppSelector } from "../../store";
import { useGetTreeViewQuery } from "../../services/TreeView/TreeViewAPI";
import { TreeViewResponse, TreeNodeData, TreeViewParams } from "../../services/TreeView/TreeViewAPI.types";
import { storedLocale } from "../Authentication/util";

export const defaultTreeViewData = (): TreeViewResponse => {
  return {
    treeData: null,
    error: null,
    callResults: [],
  };
};

export const losParams: Partial<TreeViewParams> = {
  IncludeAboAttr: true,
  IncludeAwardLevels: true,
  IncludeFrontline: true,
  IncludeGlobalMB: true,
  IncludeHasDownlineFlag: true,
  IncludeInactive: false,
  IncludeInternational: true,
  IncludeMap: true,
  IncludeSponStats: true,
  IncludeVolumeDetails: true,
};

/**
 * LOS tree params for a market's rwlos.uiGrid. The tree follows the same flag as the LOS list view:
 * markets without `dataShowInternationalAbos` (e.g. VN, ABOT-13821) never fetch international ABOs.
 */
export const getLosParamsForGrid = (uiGrid: string | undefined): Partial<TreeViewParams> => {
  const showInternationalAbos = (uiGrid ?? "")
    .split(",")
    .map((flag) => flag.trim())
    .includes("dataShowInternationalAbos");
  return showInternationalAbos ? losParams : { ...losParams, IncludeInternational: false, IncludeGlobalMB: false };
};

export const FetchTreeViewData = (period: string, skipRequest = false, params: Partial<TreeViewParams> = {}) => {
  const { user } = useAppSelector((state) => state.boot);
  const locale = storedLocale();

  const {
    data: payloadData,
    isFetching,
    isError,
    refetch,
  } = useGetTreeViewQuery(
    {
      Aff: user.aff,
      Abo: user.abo,
      Period: period,
      IsoCountryCode: user.isoCountryCode,
      Locale: locale,
      ...params,
    },
    {
      skip: skipRequest,
    },
  );

  // Seed from RTK Query's synchronous initial value so cached data is available on the
  // first render — avoids a loading flash when remounting with already-fetched data.
  const [isLoading, setIsLoading] = useState(isFetching);
  const [errorStatus, setErrorStatus] = useState(isError);
  const [treeViewData, setTreeViewData] = useState<TreeViewResponse>(payloadData ?? defaultTreeViewData());

  useEffect(() => {
    setIsLoading(isFetching);
    setErrorStatus(isError);

    if (!isFetching && !isError && payloadData) {
      setTreeViewData(payloadData);
    }
  }, [isFetching, period, isError, payloadData]);

  return { isLoading, treeViewData, errorStatus, refetch };
};

export type { TreeNodeData, TreeViewResponse, TreeViewParams };
